import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import Drawer from '../Common/Drawer/Drawer';
import BaselineTaskForm from './BaselineTaskForm';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../../components/Common/Notification';
import { DATE_FORMAT } from '../../constants/constants';
import Moment from 'moment';
import _ from 'lodash';
import {
	BaselineTaskDrawerProps,
	BaselineTaskDrawerEdit,
	NumericValue,
} from '../../apis/baselineTaskDrawer/baselineTaskDrawer.types';
import {
	useSaveBaselineTask,
	useUpdateContingent,
	useOptionSearch,
	useGetBaselineTask,
	BaselineTask,
} from '../../apis/baselineTasks';
import isEqual from '../../utils/isEqual';

// Error Fallback Component
const ErrorFallback: React.FC<{ error: unknown; resetErrorBoundary: () => void }> = ({
	error,
	resetErrorBoundary
}) => (
	<div className="alert alert-danger m-3">
		<h5>Something went wrong with the task drawer:</h5>
		<p>{error instanceof Error ? error.message : String(error)}</p>
		<button className="btn btn-secondary" onClick={resetErrorBoundary}>
			Try again
		</button>
	</div>
);

// Main Content Component
const BaselineTaskDrawerContent: React.FC<BaselineTaskDrawerProps> = ({
	subscriptionId,
	taskId,
	baselineId,
	onComplete,
	onCancel,
	onUpdateList,
	formFilters,
	isMobile,
}) => {
	// Tanstack Query hooks
	const { mutate: saveBaselineTask, isPending: isSaving } = useSaveBaselineTask((result) => {
		if (result.isError || result.errors?.length > 0) {
			const messages = result.errors?.length > 0
				? result.errors.map(e => e.message).join(' ')
				: 'An unknown error occurred';
			Notification('error', 'Save Failed', messages);
			// Reset save-and-new flag so the drawer stays open
			if (saveAndNewRetention) setSaveAndNewRetention(false);
			return;
		}

		if (saveAndNewRetention) {
			Notification('success', 'Save Successful', 'Task saved successfully');
			// Retain properties and keep drawer open for new task
			const retainProps = {
				isMilestone: editTask.isMilestone,
				jobTitleId: editTask.jobTitleId,
				departmentId: editTask.departmentId,
				communityId: editTask.communityId,
				phaseId: editTask.phaseId,
			};
			const newTaskRetain = { ...newTaskTemplate, ...retainProps };
			setEditTask(newTaskRetain);
			setUnchangedTask(newTaskRetain);
			setIsFormValid(true);
			setUnsavedChanges(false);
			setSaveAndNewRetention(false);
			onUpdateList?.();
		} else {
			Notification('success', 'Save Successful', 'Task saved successfully');
			onComplete();
		}
	});

	const updateContingentMutation = useUpdateContingent();
	const optionSearchMutation = useOptionSearch();

	// Use the hook pattern like the controller
	const { data: taskData, isFetching: isLoadingTask, error: taskLoadError } = useGetBaselineTask(
		taskId || 0,
		{ enabled: !!taskId && taskId > 0 }
	);
	// Create initial new task template (memoized to prevent re-renders)
	const newTaskTemplate = useMemo((): BaselineTaskDrawerEdit => ({
		id: 0,
		name: '',
		contingentId: null,
		phaseId: 0,
		departmentId: 0,
		communityId: 0,
		jobTitleId: 0,
		contingentName: '',
		baselineId: baselineId,
		contactName: '',
		contactEmail: '',
		additionalInfo: '',
		taskTagIds: [],
		isActive: true,
		isMilestone: false,
		isLocationTaskListActive: true,
		isLocationTaskDateAdjustable: formFilters.isProjectWidgetActive ? false : true,
		links: [],
		targetDaysOut: null,
		targetDays: null,
		targetWeeks: null,
		targetStaticDate: '',
		startDaysOut: null,
		startDays: null,
		startWeeks: null,
		startStaticDate: '',
		anticipatedEffort: '',
		steps: [],
		prereqTasks: [],
		workflows: [],
		eventId: null,
		actionId: null,
		workflowEmails: [],
		showStaticDates: false,
		createdByName: '',
		createdDate: '',
		dateCreated: '',
		modifiedByName: '',
		modifiedDate: '',
		dateModified: '',
		callWebhookOnStatusChange: false,
		isBaselineTaskValidationRulesActive: formFilters.isBaselineTaskValidationRulesActive || false,
	}), [baselineId, formFilters.isProjectWidgetActive, formFilters.isBaselineTaskValidationRulesActive]);

	// Individual state hooks
	const [editTask, setEditTask] = useState<BaselineTaskDrawerEdit>(newTaskTemplate);
	const [unchangedTask, setUnchangedTask] = useState<BaselineTaskDrawerEdit>(newTaskTemplate);
	const [isFormValid, setIsFormValid] = useState<boolean>(true);
	const [showDateError, setShowDateError] = useState<boolean>(false);
	const [taskSearchResults, setTaskSearchResults] = useState<any[]>([]);
	const [taskPrereqSearchResults, setTaskPrereqSearchResults] = useState<any[]>([]);
	const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
	const [saveAndNewRetention, setSaveAndNewRetention] = useState<boolean>(false);

	// Handle task loading error
	useEffect(() => {
		if (taskLoadError) {
			Notification('error', 'Error Loading Task', taskLoadError.message);
		}
	}, [taskLoadError]);

	// Handle task data loading and transformation (like controller pattern)
	useEffect(() => {
		if (taskData) {
			let task = { ...taskData } as any;

			// Format dates and calculate derived values
			if (task.targetStaticDate || task.startStaticDate) {
				task.showStaticDates = true;
			}
			if (task.targetStaticDate) {
				const targetDate = new Date(task.targetStaticDate);
				task.targetStaticDate = Moment.utc(targetDate).format(DATE_FORMAT);
			}
			if (task.startStaticDate) {
				const startDate = new Date(task.startStaticDate);
				task.startStaticDate = Moment.utc(startDate).format(DATE_FORMAT);
			}
			const targetDaysOut = task.targetDaysOut || 0;
			const targetSign = Math.sign(targetDaysOut);
			task.targetWeeks = targetSign * Math.floor(Math.abs(targetDaysOut) / 7);
			task.targetDays = targetSign * (Math.abs(targetDaysOut) % 7);

			if (task.startDaysOut == null) {
				task.startWeeks = null;
				task.startDays = null;
			} else {
				const startDaysOut = task.startDaysOut;
				const startSign = Math.sign(startDaysOut);
				task.startWeeks = startSign * Math.floor(Math.abs(startDaysOut) / 7);
				task.startDays = startSign * (Math.abs(startDaysOut) % 7);
			}
			if (task.createdDate) {
				const dateCreated = new Date(task.createdDate);
				task.dateCreated = Moment.utc(dateCreated).format(DATE_FORMAT);
			}
			if (task.modifiedDate) {
				const dateModified = new Date(task.modifiedDate);
				task.dateModified = Moment.utc(dateModified).format(DATE_FORMAT);
			} else {
				task.dateModified = '';
			}
			const newTaskFromLoad = { ...newTaskTemplate, ...task };
			if (!newTaskFromLoad.links || !newTaskFromLoad.links.length) {
				newTaskFromLoad.links = newTaskTemplate.links;
			}
			// Only update state if changed
			if (!isEqual(editTask, newTaskFromLoad)) setEditTask(newTaskFromLoad);
			if (!isEqual(unchangedTask, newTaskFromLoad)) setUnchangedTask(newTaskFromLoad);
			setIsFormValid(true);
		} else if (!taskId || taskId === 0) {
			if (!isEqual(editTask, newTaskTemplate)) setEditTask(newTaskTemplate);
			if (!isEqual(unchangedTask, newTaskTemplate)) setUnchangedTask(newTaskTemplate);
			setIsFormValid(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [taskData, taskId, newTaskTemplate]);

	// Search tasks functions
	const searchPrereqTasks = useCallback(async (searchTerm: string) => {
		try {
			optionSearchMutation.mutate({
				searchTerm,
				baselineId: editTask.baselineId,
				baselineTaskId: editTask.id,
				isPrereqQuery: true
			}, {
				onSuccess: (response) => {
					setTaskPrereqSearchResults(response.results);
				},
				onError: (error: any) => {
					Notification('error', 'An Error Occurred', error.message || 'Failed to search tasks');
				}
			});
		} catch (error) {
			Notification('error', 'Error', error);
		}
	}, [optionSearchMutation, editTask.baselineId, editTask.id]);

	const searchTasks = useCallback(async (searchTerm: string) => {
		try {
			optionSearchMutation.mutate({
				searchTerm,
				baselineId: editTask.baselineId,
				baselineTaskId: editTask.id,
				isPrereqQuery: false
			}, {
				onSuccess: (response) => {
					setTaskSearchResults(response.results);
				},
				onError: (error: any) => {
					Notification('error', 'An Error Occurred', error.message || 'Failed to search tasks');
				}
			});
		} catch (error) {
			Notification('error', 'Error', error);
		}
	}, [optionSearchMutation, editTask.baselineId, editTask.id]);

	// Form change handler
	const onFormChange = useCallback((name: string, value: any) => {
		setEditTask(prevTask => {
			let newTaskUpdate = {
				...prevTask,
				[name]: value,
			};

			if (name === 'isActive' && value === false) {
				newTaskUpdate = {
					...newTaskUpdate,
					isLocationTaskListActive: false,
				};
			} else if (name === 'showStaticDates') {
				newTaskUpdate = {
					...newTaskUpdate,
					isLocationTaskDateAdjustable: value === true ? false : true,
				};
			}

			return newTaskUpdate;
		});
	}, []);

	// Check for changes function
	const checkForChanges = useCallback(() => {
		setUnsavedChanges(prevUnsaved => {
			if (editTask.id !== 0) {
				const haveTheSameValues = isEqual(unchangedTask, editTask);
				return !haveTheSameValues;
			}
			return prevUnsaved;
		});
	}, [editTask, unchangedTask]);

	// Contingent change handlers
	const onContingentChange = useCallback(async (contingentId: number, contingentName: string) => {
		if (contingentId === editTask.id) {
			Notification('error', 'Invalid Contingent', 'You can not make this task a contingent upon itself.');
			return;
		}

		try {
			updateContingentMutation.mutate({
				taskId: editTask.id,
				contingentId: contingentId
			}, {
				onSuccess: () => {
					Notification('success', '', 'Contingent Updated');
					setEditTask(prev => ({ ...prev, contingentId: contingentId, contingentName: contingentName }));
					checkForChanges();
				},
				onError: (error: any) => {
					Notification('error', 'Error', error.message || 'Failed to update contingent');
				}
			});
		} catch (error) {
			Notification('error', 'Error', error);
		}
	}, [updateContingentMutation, editTask.id, checkForChanges]);

	const onContingentRemove = useCallback(async () => {
		try {
			updateContingentMutation.mutate({
				taskId: editTask.id,
				contingentId: null
			}, {
				onSuccess: () => {
					Notification('success', '', 'Contingent Removed');
					setEditTask(prev => ({ ...prev, contingentId: null, contingentName: '' }));
					checkForChanges();
				},
				onError: (error: any) => {
					Notification('error', 'Error', error.message || 'Failed to remove contingent');
				}
			});
		} catch (error) {
			Notification('error', 'Error', error);
		}
	}, [updateContingentMutation, editTask.id, checkForChanges]);

	// Prerequisite change handlers
	const onPrereqChange = useCallback(async (prereqId: number) => {
		try {
			updateContingentMutation.mutate({
				taskId: prereqId,
				contingentId: editTask.id
			}, {
				onSuccess: () => {
					Notification('success', '', 'Prereq Added');
					let prereqTasks: any[] = [];
					if (prereqId) {
						const task = taskPrereqSearchResults.find((x) => x.id === prereqId);
						if (task) {
							prereqTasks = [...(editTask.prereqTasks || []), { id: task.id, name: task.name }];
						}
					}

					setEditTask(prev => ({ ...prev, prereqTasks: prereqTasks }));
					checkForChanges();
				},
				onError: (error: any) => {
					Notification('error', 'Error', error.message || 'Failed to add prerequisite');
				}
			});
		} catch (error) {
			Notification('error', 'Error', error);
		}
	}, [updateContingentMutation, editTask.id, editTask.prereqTasks, taskPrereqSearchResults, checkForChanges]);

	const onRemovePrereq = useCallback(async (prereqId: number) => {
		try {
			updateContingentMutation.mutate({
				taskId: prereqId,
				contingentId: null
			}, {
				onSuccess: () => {
					if (editTask.prereqTasks) {
						Notification('success', '', 'Prereq Removed');
						const index = editTask.prereqTasks.findIndex((task: any) => task.id === prereqId);
						const prereqTasks = editTask.prereqTasks.slice();
						prereqTasks.splice(index, 1);

						setEditTask(prev => ({ ...prev, prereqTasks: prereqTasks }));
						checkForChanges();
					}
				},
				onError: (error: any) => {
					Notification('error', 'Error', error.message || 'Failed to remove prerequisite');
				}
			});
		} catch (error) {
			Notification('error', 'Error', error);
		}
	}, [updateContingentMutation, editTask.prereqTasks, checkForChanges]);

	// Utility functions
	const parseOrNull = useCallback((value: any): NumericValue => {
		const parsed = parseInt(value);
		return value === null || value === '' || isNaN(parsed) ? null : parsed;
	}, []);

	// Save handlers
	const onSaveAndNew = useCallback(() => {
		setSaveAndNewRetention(true);
	}, []);

	const validateForm = (): boolean => {
		const { name, jobTitleId, departmentId, communityId } = editTask;

		// Required field validation
		if (!name || name.trim().length === 0 || !jobTitleId || !departmentId || !communityId) {
			return false;
		}

		// All steps must have a non-empty name
		for (let i = 0; i < editTask.steps.length; i++) {
			if (!editTask.steps[i].name || editTask.steps[i].name === '') {
				return false;
			}
		}

		// Links: if a URL is provided, an abbreviation/name is required.
		// Clear stale whitespace so the native required indicator becomes visible.
		for (let i = 0; i < editTask.links.length; i++) {
			const link = editTask.links[i];
			if ((link.url ?? '').trim().length > 0 && (link.name ?? '').trim().length === 0) {
				const updatedLinks = editTask.links.slice();
				updatedLinks.splice(i, 1, { ...link, name: '' });
				setEditTask(prev => ({ ...prev, links: updatedLinks }));
				return false;
			}
		}

		// Email format validation
		const email = editTask.contactEmail ?? '';
		if (email.trim().length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
			Notification('error', 'Invalid Email', 'Please enter a valid contact email address.');
			return false;
		}

		// Date validation for static dates
		if (editTask.showStaticDates && !editTask.targetStaticDate) {
			Notification('error', 'Target date is required', '');
			return false;
		}

		// Days/weeks validation
		if (!editTask.showStaticDates) {
			const hasWeeks = editTask.targetWeeks !== null && !isNaN(Number(editTask.targetWeeks));
			const hasDays = editTask.targetDays !== null && !isNaN(Number(editTask.targetDays));
			if (!hasWeeks && !hasDays) {
				Notification('error', 'Please fill in at least Weeks or Days', '');
				return false;
			}
		}

		return true;
	};

	const onSave = useCallback(() => {
		if (!validateForm()) {
			setIsFormValid(false);
			setSaveAndNewRetention(false);
			return;
		}

		let task = { ...editTask };
		if (editTask.showStaticDates) {
			// Static dates path
			task.targetDaysOut = null;
			task.startDaysOut = null;
			task.targetDays = null;
			task.targetWeeks = null;
			task.startDays = null;
			task.startWeeks = null;
			task.isLocationTaskDateAdjustable = false;

			// Validate static dates
			if (formFilters.isStartDateActive) {
				if ((task.startStaticDate?.length ?? 0) > 0 || (task.targetStaticDate?.length ?? 0) > 0) {
					const start = Date.parse(task.startStaticDate ?? "");
					const target = Date.parse(task.targetStaticDate ?? "");

					if (target < start) {
						setShowDateError(true);
						setSaveAndNewRetention(false);
						return;
					}
				}
			}
		} else {
			// Days and weeks path
			const targetWeeks = parseOrNull(task.targetWeeks);
			const targetDays = parseOrNull(task.targetDays);
			const startWeeks = parseOrNull(task.startWeeks);
			const startDays = parseOrNull(task.startDays);

			// Convert to total days
			if ((targetWeeks === null || isNaN(targetWeeks)) && (targetDays === null || isNaN(targetDays))) {
				task.targetDaysOut = 0;
			} else {
				task.targetDaysOut = (targetWeeks || 0) * 7 + (targetDays || 0);
			}

			if (startWeeks === null && startDays === null) {
				task.startDaysOut = null;
			} else {
				task.startDaysOut =
					(startWeeks !== null && !isNaN(startWeeks) ? startWeeks : 0) * 7 +
					(startDays !== null && !isNaN(startDays) ? startDays : 0);
			}

			// Clear static dates
			task.startStaticDate = null;
			task.targetStaticDate = null;

			if (formFilters.isStartDateActive) {
				if (task.startDaysOut !== null && task.targetDaysOut !== null) {
					if (task.startDaysOut < task.targetDaysOut) {
						setShowDateError(true);
						setSaveAndNewRetention(false);
						return;
					}
				}
			} else {
				task.startDaysOut = null;
			}
		}
		// Additional processing
		if (task.isActive) {
			task.isLocationTaskListActive = true;
		}

		// Remove blank links
		if (task.links && task.links.length) {
			task.links = task.links.filter((x: any) => x.name && x.name.trim().length && x.url && x.url.trim().length);
		}

		// Transform taskTagIds
		if (task.taskTagIds && task.taskTagIds.length > 0) {
			task.taskTagIds = (!Array.isArray(task.taskTagIds)
				? (task.taskTagIds as string)
				: task.taskTagIds.join())
				.split(',').map((id: string) => parseInt(id, 10));

		}

		// Transform task for API
		const taskForApi: BaselineTask = {
			...task,
			id: task.id,
			name: task.name,
			isActive: task.isActive,
			isDeprecated: false,
			isMilestone: task.isMilestone,
			links: task.links || [],
			tags: [], // Will be populated from taskTagIds if needed
			totalDependencies: 0,
			totalSubtasks: 0,
			createdDate: task.createdDate || new Date().toISOString(),
			targetDaysOut: task.targetDaysOut,
			anticipatedEffort: task.anticipatedEffort,
			additionalInfo: task.additionalInfo,
			isLocationTaskListActive: task.isLocationTaskListActive ?? true,
			includeCertificationCheckbox: task.includeCertificationCheckbox ?? false,
			prereqTasks: task.prereqTasks || [],
			sameNameCount: task.sameNameCount || 0,
			workflows: task.workflows || [],
			contactName: task.contactName,
			contactEmail: task.contactEmail,
			baselineJobTitle: '', // These come from IDs, not direct strings
			department: '',
			community: '',
			discipline: '',
			baselineId: task.baselineId,
			targetStaticDate: task.targetStaticDate,
			startStaticDate: task.startStaticDate,
			startDaysOut: task.startDaysOut,
			isStartDateActive: formFilters.isStartDateActive ?? false,
			// Convert 0 values to null for optional foreign key fields
			phaseId: (task.phaseId ?? 0) > 0 ? task.phaseId : null,
			jobTitleId: task.jobTitleId,
			departmentId: task.departmentId,
			communityId: task.communityId,
			taskTagIds: task.taskTagIds ?? []
		};

		setIsFormValid(true);

		// Save the task
		saveBaselineTask(taskForApi);
	}, [editTask, formFilters.isStartDateActive, parseOrNull, saveBaselineTask]);

	// Handle save and new
	useEffect(() => {
		if (saveAndNewRetention) {
			onSave();
		}
	}, [saveAndNewRetention, onSave]);

	// Check for changes when editTask changes
	useEffect(() => {
		if (editTask.id !== 0) {
			const haveTheSameValues = isEqual(unchangedTask, editTask);
			setUnsavedChanges(!haveTheSameValues);
		}
	}, [editTask, unchangedTask]);

	// Loading state from hook
	const isLoading = isLoadingTask || isSaving;

	// Footer render function
	const renderFooter = useCallback(() => {
		if (isMobile) {
			return (
				<div>
					<div className="button-wrap">
						{editTask.id > 0 && unsavedChanges ? <span>You have unsaved changes</span> : ''}
					</div>
					<div className="button-wrap">
						<button type="button" className="btn btn-primary button--save" onClick={onSave}>
							{editTask.id === 0 ? 'Save' : 'Apply Changes'}
						</button>
						<button type="button" className="btn btn-secondary" onClick={onCancel}>
							Cancel
						</button>
					</div>
				</div>
			);
		} else {
			return (
				<div className="button-wrap">
					{editTask.id > 0 && unsavedChanges ? <span>You have unsaved changes</span> : ''}
					<button type="button" className="btn btn-primary button--save" onClick={onSave}>
						{editTask.id === 0 ? 'Save' : 'Apply Changes'}
					</button>
					{editTask.id === 0 && (
						<button type="button" className="btn btn-primary button--saveNew" onClick={onSaveAndNew}>
							Save and New
						</button>
					)}
					<button type="button" className="btn btn-link-custom" onClick={onCancel}>
						Cancel
					</button>
				</div>
			);
		}
	}, [isMobile, editTask.id, unsavedChanges, onSave, onCancel, onSaveAndNew]);

	const customFooter = renderFooter();

	return (
		<div>
			<Drawer
				isWide={true}
				title="Baseline Task"
				onSaveClick={onSave}
				saveText="Save"
				onCancelClick={onCancel}
				customFooter={customFooter}
			>
				<LoadingIndicator isLoading={isLoading}>
					<BaselineTaskForm
						task={editTask}
						isFormValid={isFormValid}
						showDateError={showDateError}
						onFormChange={onFormChange}
						onContingentChange={onContingentChange}
						onContingentRemove={onContingentRemove}
						onRemovePrereq={onRemovePrereq}
						onPrereqChange={onPrereqChange}
						formFilters={formFilters}
						taskSearch={searchTasks}
						onSave={onSave}
						taskPrereqSearch={searchPrereqTasks}
						taskSearchResults={taskSearchResults}
						taskPrereqSearchResults={taskPrereqSearchResults}
						subscriptionId={subscriptionId}
						isMobile={isMobile}
					/>
				</LoadingIndicator>
			</Drawer>
		</div>
	);
};

// Main Export Component with Error Boundary
const BaselineTaskDrawer: React.FC<BaselineTaskDrawerProps> = (props) => {
	return (
		<ErrorBoundary
			FallbackComponent={ErrorFallback}
			onReset={() => window.location.reload()}
		>
			<BaselineTaskDrawerContent {...props} />
		</ErrorBoundary>
	);
};

export default BaselineTaskDrawer;